// Value-tree utilities: build default values from a definition, read/write by
// path, and traverse the field tree. Pure functions — no React.

import {
  type DynamicFormDefinition,
  type DynamicFormField,
  type DynamicFormValue,
  ROW_KEY,
  isAlertMarkup,
  isBooleanToggle,
  isGroupField,
  isRepeatableGroup,
} from './types'
import { type ResolutionLevel, isFieldVisible } from './visibility'

// --- Row keys ---------------------------------------------------------------

/**
 * Stable uuid for a repeatable-group row. Used as the React `key` so rows
 * reconcile across add / remove / reorder. Stripped before values are emitted
 * (see {@link stripRowKeys}) and regenerated on mount.
 */
export function newRowKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Fallback for environments without crypto.randomUUID.
  return 'row-' + Math.abs(hashString(String(rowKeyCounter++))).toString(36)
}

let rowKeyCounter = 0

function hashString(input: string): number {
  let h = 0
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i)
    h |= 0
  }
  return h
}

/** Narrow a value-tree slot to the object at that level; anything else (a leaf,
 *  an array, absent) reads as an empty level. */
export function asObject(value: unknown): DynamicFormValue {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as DynamicFormValue)
    : {}
}

// --- Path parsing -----------------------------------------------------------

export type PathSegment = string | number

/**
 * Parse a field path into segments. Supports dot notation and bracketed
 * repeatable-group indices: "contacts[0].email" -> ["contacts", 0, "email"].
 */
export function parsePath(path: string): PathSegment[] {
  const segments: PathSegment[] = []
  const re = /[^.[\]]+|\[(\d+)\]/g
  let match: RegExpExecArray | null
  while ((match = re.exec(path)) !== null) {
    if (match[1] !== undefined) segments.push(Number(match[1]))
    else segments.push(match[0])
  }
  return segments
}

// --- Read / write -----------------------------------------------------------

/** Read a value by path. Returns undefined if any segment is missing. */
export function getFieldValue(value: DynamicFormValue, path: string): unknown {
  let current: unknown = value
  for (const segment of parsePath(path)) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<PathSegment, unknown>)[segment]
  }
  return current
}

/**
 * Return a new value tree with `path` set to `next`. Intermediate objects and
 * arrays are created as needed; existing siblings are preserved (immutable
 * update).
 */
export function setFieldValue(
  value: DynamicFormValue,
  path: string,
  next: unknown,
): DynamicFormValue {
  const segments = parsePath(path)
  if (segments.length === 0) return value
  return setIn(value, segments, next) as DynamicFormValue
}

function setIn(target: unknown, segments: PathSegment[], next: unknown): unknown {
  if (segments.length === 0) return next
  const [head, ...rest] = segments
  if (typeof head === 'number') {
    const arr = Array.isArray(target) ? target.slice() : []
    arr[head] = setIn(arr[head], rest, next)
    return arr
  }
  const base: Record<string, unknown> =
    target != null && typeof target === 'object' && !Array.isArray(target)
      ? { ...(target as Record<string, unknown>) }
      : {}
  base[head] = setIn(base[head], rest, next)
  return base
}

// --- Default values ---------------------------------------------------------

/** Build the initial value tree from a definition's default values. */
export function createDefaultValue(definition: DynamicFormDefinition): DynamicFormValue {
  return defaultsForFields(definition.fields)
}

function defaultsForFields(fields: DynamicFormField[]): DynamicFormValue {
  const result: DynamicFormValue = {}
  for (const field of fields) {
    if (!field.key) continue
    result[field.key] = defaultForField(field)
  }
  return result
}

function defaultForField(field: DynamicFormField): unknown {
  if (isGroupField(field)) {
    if (isRepeatableGroup(field)) {
      const min = field.config?.repeat_min ?? 0
      const rows: DynamicFormValue[] = []
      for (let i = 0; i < min; i++) rows.push(newGroupRow(field))
      return rows
    }
    return defaultsForFields(field.children ?? [])
  }

  if (field.default_value !== undefined) return field.default_value
  if (isAlertMarkup(field)) return {}

  switch (field.type) {
    case 'multi_select':
    case 'multi_checkbox':
    case 'scalar_list':
    case 'upload':
      return []
    case 'boolean':
      // A checkbox starts unticked; a toggle starts with no answer at all, so
      // a required one still has something for validation to catch.
      return isBooleanToggle(field) ? undefined : false
    default:
      return undefined
  }
}

/** A fresh repeatable-group row: child defaults plus an internal row key. */
export function newGroupRow(group: DynamicFormField): DynamicFormValue {
  const children = isGroupField(group) ? (group.children ?? []) : []
  return { ...defaultsForFields(children), [ROW_KEY]: newRowKey() }
}

// --- Traversal --------------------------------------------------------------

export type FieldVisitor = (field: DynamicFormField, ancestors: DynamicFormField[]) => void

/**
 * Depth-first visit of every field, including nested group children. The
 * visitor receives the ancestor chain (root-first, excluding the field itself).
 */
export function traverseFields(
  definition: DynamicFormDefinition,
  visitor: FieldVisitor,
): void {
  walk(definition.fields, [], visitor)
}

function walk(
  fields: DynamicFormField[],
  ancestors: DynamicFormField[],
  visitor: FieldVisitor,
): void {
  for (const field of fields) {
    visitor(field, ancestors)
    if (isGroupField(field) && field.children?.length) {
      walk(field.children, [...ancestors, field], visitor)
    }
  }
}

// --- Emit helpers -----------------------------------------------------------

/**
 * Deep clone of a value tree with all internal {@link ROW_KEY} entries removed.
 * Applied before values leave the renderer via onChange / getValue.
 */
export function stripRowKeys<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripRowKeys(item)) as unknown as T
  }
  if (value != null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === ROW_KEY) continue
      out[k] = stripRowKeys(v)
    }
    return out as T
  }
  return value
}

export function pruneHiddenValues(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
): DynamicFormValue {
  return pruneLevel(definition.fields, value, [{ fields: definition.fields, value }])
}

function pruneLevel(
  fields: DynamicFormField[],
  valueObj: Record<string, unknown>,
  levels: ResolutionLevel[],
): Record<string, unknown> {
  const out = { ...valueObj }
  const visibleFields = new Set(fields.filter((field) => isFieldVisible(field, levels)))
  const visibleKeys = new Set([...visibleFields].map((field) => field.key))
  for (const field of fields) {
    if (!field.key || !(field.key in out)) continue
    if (!visibleKeys.has(field.key)) {
      delete out[field.key]
      continue
    }
    if (!visibleFields.has(field) || !isGroupField(field)) continue

    const children = field.children ?? []
    const fieldValue = out[field.key]
    if (isRepeatableGroup(field)) {
      if (!Array.isArray(fieldValue)) continue
      out[field.key] = fieldValue.map((row) => {
        if (row == null || typeof row !== 'object' || Array.isArray(row)) return row
        const rowObj = row as Record<string, unknown>
        return pruneLevel(children, rowObj, [...levels, { fields: children, value: rowObj }])
      })
    } else if (fieldValue != null && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      const childObj = fieldValue as Record<string, unknown>
      out[field.key] = pruneLevel(children, childObj, [...levels, { fields: children, value: childObj }])
    }
  }
  return out
}
